import { type ReactNode } from 'react'
import { isNotEmpty } from '@terros/common'
import { Center, Group, Image, SimpleGrid, Stack, Text } from '@mantine/core'
import { Theme, useTranslation } from '@front/model'
import { MaterialSymbolIcon } from '@components'
import { type PreviewGalleryImage, type PreviewLayout } from './previewContent'

const MOBILE_IMAGE_WIDTH = 176

type Props = {
  images: PreviewGalleryImage[]
  layout: PreviewLayout
  ink: string
}

export const PreviewGallery = ({ images, layout, ink }: Props): ReactNode => {
  const { t } = useTranslation()
  const isMobile = layout === 'mobile'

  return (
    <Stack gap='sm'>
      <Group align='baseline' justify='space-between'>
        <Text c={ink} fw='bold' size='lg'>
          {t('Our work')}
        </Text>
        {isMobile && (
          <Text c={Theme.light.text.neutral.tertiary} size='sm'>
            {t('Swipe')}
          </Text>
        )}
      </Group>
      {isMobile ? (
        <Group gap='sm' wrap='nowrap' style={{ overflow: 'hidden' }}>
          {images.map((image) => (
            <GalleryTile key={image.key} image={image} width={MOBILE_IMAGE_WIDTH} />
          ))}
        </Group>
      ) : (
        <SimpleGrid cols={3} spacing='sm'>
          {images.map((image) => (
            <GalleryTile key={image.key} image={image} />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  )
}

const GalleryTile = ({ image, width }: { image: PreviewGalleryImage; width?: number }): ReactNode => {
  const { t } = useTranslation()
  if (isNotEmpty(image.url)) {
    return (
      <Image
        alt={t('Company gallery image')}
        fit='cover'
        radius='xl'
        src={image.url}
        style={{ aspectRatio: 4 / 3 }}
        w={width}
      />
    )
  }
  return (
    <Center bg={Theme.light.background.neutral.tertiary} bdrs='xl' miw={width} style={{ aspectRatio: 4 / 3 }} w={width}>
      <MaterialSymbolIcon color={Theme.light.icon.neutral.secondary} icon='photo' size='xl' />
    </Center>
  )
}
